import React from 'react';

/**
 * Paramétrage → sous-onglet « Configuration » (lot 5.4).
 *
 * Les trois cartes de la maquette (`#pane-config`) : année & département, types de
 * journées, niveaux/groupes/heures. Remplace `SetupHeader` (partie basse),
 * `SetupTypesJournees` et `SetupNiveaux`, qui portaient à eux trois 15 emojis et une
 * mise en page qui n'était pas celle de la maquette.
 */

// ── Carte 1 : année universitaire & département ──────────────────────────────

function CarteAnnee({
    allAnnees, anneeSelectionnee, anneeAttendue,
    departements, selectedDept, setSelectedDept, isSingleDept, loadingExisting,
    titrePlanning, setTitrePlanning,
    rappelSauvegarde, setRappelSauvegarde,
    lectureSeule,
}) {
    return (
        <div className="pl-card">
            <div className="pl-card-header">
                <span className="ttl"><i className="fa-solid fa-calendar" /> Année universitaire &amp; département</span>
            </div>
            <div className="pl-card-body">
                <div className="pl-cfg-grid">

                    {/* On ne CRÉE pas une année ici (lot 3 du Bloc 0) : elle est transverse à
                        TOUS les départements. Lot PA.1 : on ne la CHOISIT plus non plus — c'est
                        celle de la barre de gauche, choix global de l'application. Un second
                        sélecteur ici pouvait faire enregistrer la configuration sous une autre
                        année que celle affichée partout ailleurs. */}
                    <div className="pl-cfg-field">
                        <label>Année universitaire</label>
                        {allAnnees?.length > 0 ? (
                            <>
                                <input
                                    className="pl-f"
                                    readOnly
                                    value={anneeSelectionnee
                                        ? anneeSelectionnee.libelle + (anneeSelectionnee.active ? ' — année active' : '')
                                        : `${anneeAttendue} — introuvable`}
                                />
                                <span className="pl-hint">
                                    {anneeSelectionnee
                                        ? 'Choisie dans la barre de gauche ; créée dans Administration → Calendriers'
                                        : 'Cette année n\'existe pas encore : créez-la dans Administration → Calendriers'}
                                </span>
                            </>
                        ) : (
                            <span className="pl-hint" style={{ color: '#b45309' }}>
                                Aucune année universitaire n'existe. Créez-la dans
                                <strong> Administration → Calendriers</strong>, puis revenez ici.
                            </span>
                        )}
                    </div>

                    {/* Zone de vacances : champ en lecture seule, comme la maquette.
                        Elle appartient à l'année, pas au département. */}
                    <div className="pl-cfg-field">
                        <label>Zone de vacances</label>
                        <input
                            className="pl-f"
                            disabled
                            value={
                                anneeSelectionnee?.zone
                                    ? (anneeSelectionnee.zone === 'NC'
                                        ? 'Aucune vacance scolaire'
                                        : `Zone ${anneeSelectionnee.zone}`)
                                    : 'Non renseignée'
                            }
                        />
                        <span className="pl-hint">Enregistrée avec l'année, en lecture seule ici</span>
                    </div>

                    {/* ÉCART ASSUMÉ (spec §6bis) : la maquette dessine le département en
                        champ désactivé — le cas d'un utilisateur qui n'en a qu'un. Le
                        figer supprimerait à un administrateur multi-départements le seul
                        endroit où il choisit celui qu'il configure. On garde donc la
                        liste déroulante dès qu'il y a plus d'un département. */}
                    <div className="pl-cfg-field">
                        <label>Département</label>
                        {!isSingleDept ? (
                            <select
                                className="pl-f"
                                value={selectedDept?.id || ''}
                                onChange={(e) =>
                                    setSelectedDept(departements.find(d => d.id === parseInt(e.target.value, 10)))
                                }
                            >
                                {departements.map(dept => (
                                    <option key={dept.id} value={dept.id}>{dept.nom}</option>
                                ))}
                            </select>
                        ) : (
                            <input className="pl-f" disabled value={selectedDept?.nom || ''} />
                        )}
                        <span className="pl-hint">
                            {loadingExisting
                                ? <><i className="fa-solid fa-spinner fa-spin" /> Vérification…</>
                                : 'Contexte courant'}
                        </span>
                    </div>

                    <div className="pl-cfg-field pl-cfg-full">
                        <label>Titre du planning</label>
                        <input
                            className="pl-f"
                            type="text"
                            value={titrePlanning}
                            onChange={(e) => setTitrePlanning(e.target.value)}
                            placeholder="Titre de votre choix"
                            disabled={lectureSeule}
                        />
                        <span className="pl-hint">
                            Laissez vide pour le titre par défaut. Ce titre n'apparaît pas dans la barre
                            du module (écart E1) : il sert aux <strong>exports PDF</strong>.
                        </span>
                    </div>

                    {/* Rappel de sauvegarde — POSTÉRIEUR à la maquette (lot 5.3 bis).
                        Sa place définitive, annoncée alors, est ici. */}
                    <div className="pl-cfg-field">
                        <label>Rappel de sauvegarde (minutes)</label>
                        <input
                            className="pl-f"
                            type="number"
                            min="0"
                            max="240"
                            value={rappelSauvegarde}
                            onChange={(e) => setRappelSauvegarde(e.target.value)}
                            placeholder="30"
                            disabled={lectureSeule}
                        />
                        <span className="pl-hint">
                            Bandeau « pensez à enregistrer une version » quand le planning est modifié
                            en mode édition. <strong>0 = désactivé</strong> · sinon 1 à 240 minutes.
                        </span>
                    </div>

                </div>
            </div>
        </div>
    );
}

// ── Carte 2 : types de journées ──────────────────────────────────────────────

function CarteTypesJournees({ typesJournees, onAdd, onRemove, onUpdate, lectureSeule }) {
    return (
        <div className="pl-card">
            <div className="pl-card-header">
                <span className="ttl"><i className="fa-solid fa-tag" /> Types de journées</span>
                <button className="pl-btn pl-btn-sm" onClick={onAdd} disabled={lectureSeule}>
                    <i className="fa-solid fa-plus" /> Ajouter un type
                </button>
            </div>
            <div className="pl-card-body">
                <p className="pl-hint" style={{ marginBottom: 10 }}>
                    Cochez « Contrôle » pour suivre les heures dans le tableau de bord
                    (elles deviennent des colonnes ci-dessous).
                </p>
                <div className="pl-matrix-wrap">
                    <table className="pl-cfg-types">
                        <thead>
                            <tr>
                                <th>Code</th><th>Libellé</th><th>Couleur</th><th>Aperçu</th>
                                <th>Contrôle</th><th>h/jour</th><th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {typesJournees.map((type, idx) => (
                                <tr key={idx}>
                                    <td>
                                        <input
                                            type="text" maxLength={1} value={type.code}
                                            style={{ width: 42, textAlign: 'center', fontWeight: 800 }}
                                            onChange={(e) => onUpdate(idx, 'code', e.target.value)}
                                            disabled={lectureSeule}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="text" value={type.libelle} placeholder="Libellé…"
                                            style={{ width: '100%', minWidth: 140 }}
                                            onChange={(e) => onUpdate(idx, 'libelle', e.target.value)}
                                            disabled={lectureSeule}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="color" value={type.couleur}
                                            onChange={(e) => onUpdate(idx, 'couleur', e.target.value)}
                                            disabled={lectureSeule}
                                        />
                                    </td>
                                    <td>
                                        <span
                                            className="pl-cfg-apercu"
                                            style={{ background: type.couleur, color: type.couleur_texte || '#fff' }}
                                        >
                                            {type.code || '?'}
                                        </span>
                                    </td>
                                    <td style={{ textAlign: 'center' }}>
                                        <input
                                            type="checkbox" checked={!!type.controle}
                                            onChange={(e) => onUpdate(idx, 'controle', e.target.checked)}
                                            disabled={lectureSeule}
                                        />
                                    </td>
                                    <td>
                                        <input
                                            type="number" min="0" max="24" style={{ width: 58 }}
                                            value={type.heures_par_jour || 0}
                                            onChange={(e) => onUpdate(idx, 'heures_par_jour', parseInt(e.target.value, 10) || 0)}
                                            disabled={!type.controle || lectureSeule}
                                        />
                                    </td>
                                    <td>
                                        <button
                                            className="pl-icon-btn"
                                            onClick={() => onRemove(idx)}
                                            disabled={typesJournees.length <= 1 || lectureSeule}
                                            title="Supprimer ce type"
                                        >
                                            <i className="fa-solid fa-xmark" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

// ── Carte 3 : niveaux, groupes & heures par semestre ─────────────────────────

function CarteNiveaux({
    niveauxConfig, typesControles,
    onToggleNiveau, onMoveNiveau, onRemoveNiveau, onAddNiveau,
    onUpdateNiveauCode, onUpdateNiveauNom, onUpdateNbGroupes,
    onUpdateGroupeNom, onUpdateGroupeCouleur, onUpdateGroupeHeures,
    onOpenImportModal, lectureSeule,
}) {
    return (
        <div className="pl-card">
            <div className="pl-card-header">
                <span className="ttl">
                    <i className="fa-solid fa-graduation-cap" /> Niveaux, groupes &amp; heures par semestre
                </span>
                <button className="pl-btn pl-btn-sm" onClick={onAddNiveau} disabled={lectureSeule}>
                    <i className="fa-solid fa-plus" /> Ajouter un niveau
                </button>
            </div>
            <div className="pl-card-body">
                {niveauxConfig.map((niveau, nIdx) => {
                    const sem1 = niveau.semestres[0];
                    const sem2 = niveau.semestres[1];

                    return (
                        <div key={nIdx} className={'pl-niv-block' + (niveau.selected ? '' : ' off')}>
                            <div className="pl-niv-head">
                                <input
                                    type="checkbox" checked={niveau.selected}
                                    onChange={() => onToggleNiveau(nIdx)}
                                    title="Activer ce niveau" disabled={lectureSeule}
                                />
                                <input
                                    className="code pl-f" type="text" value={niveau.code} placeholder="CODE"
                                    onChange={(e) => onUpdateNiveauCode(nIdx, e.target.value)}
                                    disabled={lectureSeule}
                                />
                                <input
                                    className="nom pl-f" type="text" value={niveau.nom} placeholder="Nom du niveau…"
                                    onChange={(e) => onUpdateNiveauNom(nIdx, e.target.value)}
                                    disabled={lectureSeule}
                                />
                                {niveau.selected && (
                                    <span className="pl-niv-grp-ctrl">
                                        <span className="pl-hint">Groupes</span>
                                        <button
                                            className="pl-icon-btn"
                                            onClick={() => onUpdateNbGroupes(nIdx, niveau.nbGroupes - 1)}
                                            disabled={niveau.nbGroupes <= 1 || lectureSeule}
                                            title="Retirer un groupe"
                                        ><i className="fa-solid fa-minus" /></button>
                                        <b>{niveau.nbGroupes}</b>
                                        <button
                                            className="pl-icon-btn"
                                            onClick={() => onUpdateNbGroupes(nIdx, niveau.nbGroupes + 1)}
                                            disabled={niveau.nbGroupes >= 10 || lectureSeule}
                                            title="Ajouter un groupe"
                                        ><i className="fa-solid fa-plus" /></button>
                                    </span>
                                )}
                                <span className="pl-niv-actions">
                                    <button
                                        className="pl-icon-btn" onClick={() => onMoveNiveau(nIdx, -1)}
                                        disabled={nIdx === 0 || lectureSeule} title="Monter"
                                    ><i className="fa-solid fa-chevron-up" /></button>
                                    <button
                                        className="pl-icon-btn" onClick={() => onMoveNiveau(nIdx, 1)}
                                        disabled={nIdx === niveauxConfig.length - 1 || lectureSeule} title="Descendre"
                                    ><i className="fa-solid fa-chevron-down" /></button>
                                    <button
                                        className="pl-icon-btn" onClick={() => onRemoveNiveau(nIdx)}
                                        disabled={lectureSeule} title="Supprimer ce niveau"
                                    ><i className="fa-solid fa-trash" /></button>
                                </span>
                            </div>

                            {niveau.selected && typesControles.length === 0 && (
                                <div className="pl-hint" style={{ padding: '10px 12px' }}>
                                    <i className="fa-solid fa-triangle-exclamation" /> Activez le contrôle sur
                                    au moins un type de journée pour saisir les heures.
                                </div>
                            )}

                            {niveau.selected && typesControles.length > 0 && (
                                <div style={{ overflowX: 'auto', padding: '8px 12px' }}>
                                    <table className="pl-grp-table">
                                        <thead>
                                            <tr>
                                                <th>#</th><th></th><th>Groupe</th>
                                                <th className="sep"></th>
                                                <th colSpan={typesControles.length}>{sem1}</th>
                                                <th className="sep"></th>
                                                <th colSpan={typesControles.length}>{sem2}</th>
                                                <th></th>
                                            </tr>
                                            <tr>
                                                <th></th><th></th><th></th>
                                                <th className="sep"></th>
                                                {typesControles.map(t => (
                                                    <th key={t.code + '-1'} style={{ color: t.couleur }}>{t.code}</th>
                                                ))}
                                                <th className="sep"></th>
                                                {typesControles.map(t => (
                                                    <th key={t.code + '-2'} style={{ color: t.couleur }}>{t.code}</th>
                                                ))}
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {niveau.groupes.map((groupe, gIdx) => {
                                                const sousNiveauId = groupe.id;
                                                const sousNiveauNom = niveau.nbGroupes === 1
                                                    ? niveau.code
                                                    : niveau.code + ' ' + groupe.nom;

                                                const heuresManquantes = typesControles.some(t =>
                                                    !(groupe.semestresHeures?.[sem1]?.[t.code] > 0) ||
                                                    !(groupe.semestresHeures?.[sem2]?.[t.code] > 0)
                                                );

                                                const cellules = (sem) => typesControles.map(t => (
                                                    <td key={t.code + sem}>
                                                        <input
                                                            type="number" min="0" placeholder="0"
                                                            value={groupe.semestresHeures?.[sem]?.[t.code] ?? ''}
                                                            onChange={(e) => onUpdateGroupeHeures(nIdx, gIdx, sem, t.code, e.target.value)}
                                                            disabled={lectureSeule}
                                                        />
                                                    </td>
                                                ));

                                                return (
                                                    <tr key={gIdx} className={heuresManquantes ? 'pl-grp-warn' : ''}>
                                                        <td>#{gIdx + 1}</td>
                                                        <td>
                                                            <input
                                                                type="color" value={groupe.couleur}
                                                                onChange={(e) => onUpdateGroupeCouleur(nIdx, gIdx, e.target.value)}
                                                                disabled={lectureSeule}
                                                            />
                                                        </td>
                                                        <td>
                                                            <input
                                                                className="gnom" type="text" placeholder="Nom"
                                                                value={groupe.nom}
                                                                onChange={(e) => onUpdateGroupeNom(nIdx, gIdx, e.target.value)}
                                                                disabled={lectureSeule}
                                                            />
                                                        </td>
                                                        <td className="sep"></td>
                                                        {cellules(sem1)}
                                                        <td className="sep"></td>
                                                        {cellules(sem2)}
                                                        {/* Décision D5 — le CHIFFRE et sa colonne ont disparu.
                                                            Sur l'année N+1 la liste des étudiants n'existe pas encore :
                                                            le compteur ne pouvait afficher qu'un zéro, et un compteur
                                                            qui échoue en affichant zéro est plus trompeur qu'un
                                                            compteur absent. Il reste une PORTE D'ENTRÉE, parce que cet
                                                            écran est aujourd'hui le seul endroit de l'application qui
                                                            ouvre l'import PRONOTE.

                                                            ⚠️ CORRIGÉ le 2026-08-24 : ce commentaire annonçait que la
                                                            porte disparaissait « à la livraison du module Étudiants ».
                                                            C'est FAUX, et ça a déjà induit en erreur. PRONOTE est un
                                                            logiciel d'EMPLOI DU TEMPS : cet import affecte les
                                                            étudiants à leurs GROUPES (`groupe_pronote` →
                                                            `sous_niveau_id`), ce qui est le métier d'**EDT Builder**,
                                                            pas celui du référentiel des étudiants. Il n'est donc ni
                                                            supprimé ni repris par le module Étudiants : il
                                                            DÉMÉNAGERA dans EDT Builder, et la porte reste ici tant
                                                            que ce module n'existe pas — la retirer avant supprimerait
                                                            le seul accès à l'import.

                                                            ⚠️ Ce que le module Étudiants change quand même : APOGEE
                                                            est désormais la source de vérité sur l'IDENTITÉ (décidé
                                                            le 2026-08-14). Quand EDT Builder reprendra cet import, il
                                                            devra APPARIER les étudiants existants (par numéro, à
                                                            défaut par INE) et ne plus jamais en CRÉER — comme
                                                            l'import SCODOC. Voir doc/modules/edt_builder.md. */}
                                                        <td>
                                                            <button
                                                                className="pl-icon-btn"
                                                                onClick={() => {
                                                                    if (sousNiveauId != null) onOpenImportModal(sousNiveauId, sousNiveauNom);
                                                                }}
                                                                disabled={sousNiveauId == null || lectureSeule}
                                                                title={sousNiveauId == null
                                                                    ? "Enregistrez d'abord la configuration"
                                                                    : 'Importer les étudiants de ce groupe — provisoire, ira dans le module Étudiants'}
                                                            >
                                                                <i className="fa-solid fa-user-group" />
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>

                                    {/* ÉCART ASSUMÉ (spec §6bis) : absent de la maquette, conservé.
                                        Une heure d'objectif à 0 rend la ligne muette dans le tableau
                                        de bord — sans cet avertissement, l'indicateur paraît en panne. */}
                                    {niveau.groupes.some(g => typesControles.some(t =>
                                        !(g.semestresHeures?.[sem1]?.[t.code] > 0) ||
                                        !(g.semestresHeures?.[sem2]?.[t.code] > 0)
                                    )) && (
                                        <div className="pl-hint" style={{ marginTop: 6 }}>
                                            <i className="fa-solid fa-triangle-exclamation" /> Les heures laissées
                                            à 0 n'auront pas d'indicateur de contrôle dans le tableau de bord.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

// ── Volet complet ────────────────────────────────────────────────────────────

/**
 * Lot PA.1 — le volet est coupé en deux ÉTAPES du Planning annuel :
 *   `etape` 1 · l'année (lecture seule) et les types de journées ;
 *   `etape` 3 · les niveaux, groupes et heures par semestre.
 * Les cartes elles-mêmes ne changent pas.
 */
function ParamConfiguration({ etape, ...props }) {
    if (etape === 3) {
        return (
            <>
                <div className="pl-note">
                    <i className="fa-solid fa-circle-info" />
                    <span>
                        Les <b>formations</b> du département : niveaux, groupes et heures par semestre. Elles
                        valent pour <b>toutes les années</b> ; ce sont les colonnes du calendrier (étape 4).
                    </span>
                </div>
                <CarteNiveaux {...props} />
            </>
        );
    }
    return (
        <>
            <div className="pl-note">
                <i className="fa-solid fa-circle-info" />
                <span>
                    L'année est celle de la <b>barre de gauche</b> : elle ne se choisit ni ne se crée ici
                    (elle se crée dans <b>Administration → Calendriers</b>, avec sa zone de vacances). Les
                    types de journées, le titre et le rappel valent pour <b>toutes les années</b> du département.
                </span>
            </div>
            <CarteAnnee {...props} />
            <CarteTypesJournees {...props} />
        </>
    );
}

export default ParamConfiguration;
